import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BiArrowBack } from "react-icons/bi";
import { AiOutlinePrinter } from "react-icons/ai";
import axiosInstance from "../axiosinstance";
import API_URL from "../Config";
import "./ChecklistView.css";

const requirementTypeLabel = (value) => {
  if (value === "ALL_REQUIRED") return "All Required";
  if (value === "ANY_ONE") return "Any One";
  return "";
};

function ChecklistView({ isSidebarExpanded }) {
  const { checklist_id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [checklist, setChecklist] = useState(null);

  useEffect(() => {
    const fetchChecklistDetail = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await axiosInstance.get(`/api/checklist/${checklist_id}`);
        const data = response.data || {};

        const sections = Array.isArray(data.sections) ? data.sections : [];

        // Keep API order by default; sort only when display_order is available.
        const sortedSections = [...sections].sort((a, b) => {
          const aOrder = Number.isFinite(Number(a?.display_order))
            ? Number(a.display_order)
            : null;
          const bOrder = Number.isFinite(Number(b?.display_order))
            ? Number(b.display_order)
            : null;

          if (aOrder === null || bOrder === null) return 0;
          return aOrder - bOrder;
        });

        const normalizedSections = sortedSections.map((section) => {
          const sectionItems = Array.isArray(section?.items) ? section.items : [];
          const sortedItems = [...sectionItems].sort((a, b) => {
            const aOrder = Number.isFinite(Number(a?.display_order))
              ? Number(a.display_order)
              : null;
            const bOrder = Number.isFinite(Number(b?.display_order))
              ? Number(b.display_order)
              : null;

            if (aOrder === null || bOrder === null) return 0;
            return aOrder - bOrder;
          });

          return {
            ...section,
            items: sortedItems,
          };
        });

        setChecklist({
          id: data.id || checklist_id,
          checklist_name: data.checklist_name || "",
          checklist_description: data.checklist_description || "",
          logo: data.logo || "",
          status: data.status || "",
          sections: normalizedSections,
        });
      } catch (err) {
        console.error("Error fetching checklist detail:", err);
        setChecklist(null);
        setError(
          err.response?.data?.error ||
            err.response?.data?.message ||
            "Failed to load checklist"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchChecklistDetail();
  }, [checklist_id]);

  const pageTitle = useMemo(() => {
    if (checklist?.checklist_name) return checklist.checklist_name;
    return "Checklist View";
  }, [checklist]);

  const checklistLogoUrl = useMemo(() => {
    const rawLogo = checklist?.logo;
    if (!rawLogo || !String(rawLogo).trim()) return "";

    const normalizedPath = String(rawLogo).replace(/\\/g, "/");
    if (
      normalizedPath.startsWith("http://") ||
      normalizedPath.startsWith("https://") ||
      normalizedPath.startsWith("data:")
    ) {
      return normalizedPath;
    }

    return `${API_URL}${normalizedPath.startsWith("/") ? "" : "/"}${normalizedPath}`;
  }, [checklist]);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div
      className={`checklist-view-page ${
        isSidebarExpanded ? "expanded" : "collapsed"
      }`}
    >
      <div className="checklist-view-toolbar print-hidden">
        <button
          type="button"
          className="checklist-view-btn checklist-back-btn"
          onClick={() => navigate(-1)}
        >
          <BiArrowBack />
          <span>Back</span>
        </button>

        <button
          type="button"
          className="checklist-view-btn checklist-print-btn"
          onClick={handlePrint}
        >
          <AiOutlinePrinter />
          <span>Print</span>
        </button>
      </div>

      <div className="checklist-view-document">
        {loading ? (
          <p className="checklist-view-state">Loading checklist...</p>
        ) : error ? (
          <p className="checklist-view-state checklist-view-error">{error}</p>
        ) : !checklist ? (
          <p className="checklist-view-state">Checklist not found</p>
        ) : (
          <>
            <div className="checklist-doc-header">
              <div className="checklist-doc-header-top">
                <div className="checklist-doc-header-left">
                  <h1 className="checklist-doc-title">{pageTitle}</h1>
                  {checklist.checklist_description ? (
                    <p className="checklist-doc-description">
                      {checklist.checklist_description}
                    </p>
                  ) : null}
                </div>
                {checklistLogoUrl ? (
                  <img
                    src={checklistLogoUrl}
                    alt="Firm logo"
                    className="checklist-doc-logo"
                  />
                ) : null}
              </div>
            </div>

            <div className="checklist-doc-body">
              {checklist.sections.length === 0 ? (
                <div className="checklist-empty-message">No sections available</div>
              ) : (
                checklist.sections.map((section, sectionIndex) => (
                  <section
                    className="checklist-doc-section"
                    key={section.id || `section-${sectionIndex}`}
                  >
                    <div className="checklist-section-heading-row">
                      <h2 className="checklist-section-title">
                        {section.name || `Section ${sectionIndex + 1}`}
                      </h2>
                      {requirementTypeLabel(section.requirement_type) ? (
                        <span
                          className={`checklist-section-badge ${
                            section.requirement_type === "ALL_REQUIRED"
                              ? "all-required"
                              : "any-one"
                          }`}
                        >
                          {requirementTypeLabel(section.requirement_type)}
                        </span>
                      ) : null}
                    </div>

                    {section.section_note ? (
                      <p className="checklist-section-note">{section.section_note}</p>
                    ) : null}

                    {section.items?.length ? (
                      <ul className="checklist-items-list">
                        {section.items.map((item, itemIndex) => (
                          <li
                            className="checklist-item-row"
                            key={item.id || `item-${sectionIndex}-${itemIndex}`}
                          >
                            <div className="checklist-item-name">
                              {item.item_name || `Item ${itemIndex + 1}`}
                            </div>
                            {item.item_note ? (
                              <div className="checklist-item-note">
                                {item.item_note}
                              </div>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="checklist-empty-message">No items added</p>
                    )}
                  </section>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ChecklistView;
